<template>
  <v-card>
    <v-card-title>
      <v-btn-toggle v-model="activeTab" mandatory dense class="mr-4">
        <v-btn>
          {{ $t('dispatch.tabs.unassigned') }} ({{ unassignedOrders.length + rejectedOrders.length }})
        </v-btn>
        <v-btn>
          {{ $t('dispatch.tabs.assigned') }} ({{ assignedOrders.length }})
        </v-btn>
      </v-btn-toggle>

      <v-spacer />

      <v-text-field
        v-model="search"
        :placeholder="$t('dispatch.search')"
        prepend-inner-icon="mdi-magnify"
        single-line
        hide-details
        clearable
        dense
        outlined
        style="max-width: 300px"
      />
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :search="search"
      :custom-filter="customFilter"
      :item-class="rowClass"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
    >
      <template v-slot:[`item.code`]="{ item }">
        <span style="white-space: nowrap">{{ item.code }}</span>
      </template>

      <template v-slot:[`item.customer`]="{ item }">
        <div>{{ item.customer.firstName }} {{ item.customer.lastName }}</div>
        <div class="caption grey--text">{{ item.customer.email }}</div>
      </template>

      <template v-slot:[`item.destination`]="{ item }">
        <div>{{ item.shippingAddress.city }}</div>
        <div class="caption grey--text">{{ item.shippingAddress.province }}</div>
      </template>

      <template v-slot:[`item.orderPlacedAt`]="{ item }">
        {{ formatDate(item.orderPlacedAt) }}
      </template>

      <template v-slot:[`item.total`]="{ item }">
        {{ formatCurrency(item.total) }}
      </template>

      <template v-slot:[`item.assignTo`]="{ item }">
        <div
          v-if="item.state === 'NonAssegnato' || item.state === 'Rifiutato'"
          class="d-flex align-center"
        >
          <v-select
            v-model="selectedDealer[item.id]"
            :items="getDealerItems(item)"
            item-text="name"
            item-value="id"
            item-disabled="disabled"
            dense
            outlined
            hide-details
            class="mr-2"
            style="max-width: 200px"
          />
          <v-btn
            small
            color="primary"
            :disabled="!selectedDealer[item.id]"
            @click="$emit('assign', { orderId: item.id, dealerId: selectedDealer[item.id] })"
          >
            {{ $t('dispatch.assign') }}
          </v-btn>
        </div>
        <v-chip v-else color="success" small>
          {{ getDealerName(item.assignedTo) }}
        </v-chip>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { formatCurrency } from '@/store/modules/dealerOrders';

export default {
  name: 'OrderDispatchTable',

  props: {
    dealers: {
      type: Array,
      default: () => [],
    },
    unassignedOrders: {
      type: Array,
      default: () => [],
    },
    rejectedOrders: {
      type: Array,
      default: () => [],
    },
    assignedOrders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeTab: 0,
      search: '',
      selectedDealer: {},
    };
  },

  computed: {
    headers() {
      return [
        { text: this.$t('dispatch.headers.code'), value: 'code' },
        { text: this.$t('dispatch.headers.customer'), value: 'customer', sortable: false },
        { text: this.$t('dispatch.headers.destination'), value: 'destination', sortable: false },
        { text: this.$t('dispatch.headers.date'), value: 'orderPlacedAt' },
        { text: this.$t('dispatch.headers.total'), value: 'total', align: 'end' },
        {
          text: this.$t('dispatch.headers.assignTo'),
          value: 'assignTo',
          sortable: false,
          width: '280px',
        },
      ];
    },

    filteredItems() {
      switch (this.activeTab) {
        case 0:
          return [...this.unassignedOrders, ...this.rejectedOrders];
        case 1:
          return this.assignedOrders;
        default:
          return [];
      }
    },
  },

  methods: {
    formatCurrency,

    customFilter(_value, search, item) {
      if (!search) return true;
      const q = search.toLowerCase();
      const customerName = `${item.customer.firstName} ${item.customer.lastName}`.toLowerCase();
      const customerEmail = (item.customer.email || '').toLowerCase();
      const code = (item.code || '').toLowerCase();
      return customerName.includes(q) || customerEmail.includes(q) || code.includes(q);
    },

    formatDate(timestamp) {
      if (!timestamp) return '';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },

    rowClass(item) {
      if (item.state === 'NonAssegnato') return 'yellow lighten-5';
      if (item.state === 'Rifiutato') return 'pink lighten-5';
      return '';
    },

    getDealerItems(item) {
      const rejectedBy = item.rejectedBy || [];
      return this.dealers.map((dealer) => {
        const wasRejected = rejectedBy.includes(dealer.id);
        return {
          id: dealer.id,
          name: wasRejected ? `${dealer.name} — ha rifiutato` : dealer.name,
          disabled: wasRejected,
        };
      });
    },

    getDealerName(dealerId) {
      const dealer = this.dealers.find((d) => d.id === dealerId);
      return dealer ? dealer.name : dealerId || '';
    },
  },
};
</script>
